import { useState } from 'react';
import {
  Button,
  Switch,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowUndo20Regular, Save20Regular } from '@fluentui/react-icons';
import { JsonEditor } from '@/components/WorkflowDesigner/JsonEditor';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { ParametersFormEditor } from './ParametersFormEditor';
import type { ParametersEditorState } from './useParametersEditor';

type EditorMode = 'ui' | 'json';

const useStyles = makeStyles({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Keep horizontal overflow visible so tab focus isn't clipped by tabList's negative margin.
    overflowY: 'hidden',
    height: '100%',
  },
  modeBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
    flexShrink: 0,
    // Fixed width label avoids layout shift when toggling Draft/Production
    '& .fui-Switch__label': {
      minWidth: '70px',
    },
  },
  tabList: {
    // Fluent <Tab size="small"> ships with intrinsic horizontal padding of
    // `spacingHorizontalSNudge`. Pulling the TabList left by the same token
    // lands the first tab's content flush with the section <h2> above.
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalSNudge})`,
  },
  editorSurface: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  jsonError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
  footerActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

export interface ParametersViewProps {
  state: ParametersEditorState;
}

/**
 * Parameters editor surface. Renders either a form view or a JSON view of the
 * same underlying state — the parent supplies the JSON string and change
 * callback, so Save / Discard / draft-mode work identically regardless of
 * which mode is active.
 */
export const ParametersView = ({ state }: ParametersViewProps) => {
  const styles = useStyles();
  const {
    userCanEdit,
    permissionsLoading,
    isLoading,
    isError,
    refetch,
    isDraftMode,
    setIsDraftMode,
    rawJson,
    jsonError,
    isValidJson,
    handleJsonChange,
  } = state;

  const [mode, setMode] = useState<EditorMode>('ui');

  if (isLoading) {
    return (
      <div className={styles.root}>
        <TableSkeleton rows={4} columns={2} label="Loading parameters..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>
          <Text>
            Failed to load parameters.{' '}
            <Button appearance="transparent" onClick={() => refetch()}>
              Retry
            </Button>
          </Text>
        </div>
      </div>
    );
  }

  const readOnly = permissionsLoading || !userCanEdit;

  return (
    <div className={styles.root}>
      <div className={styles.modeBar}>
        <TabList
          className={styles.tabList}
          aria-label="Parameters editor mode"
          selectedValue={mode}
          onTabSelect={(_, data) => setMode(data.value as EditorMode)}
          size="small"
        >
          <Tab value="ui">Form</Tab>
          <Tab value="json">JSON</Tab>
        </TabList>
        <Switch
          checked={isDraftMode}
          onChange={(_, data) => setIsDraftMode(data.checked)}
          label={isDraftMode ? 'Draft mode' : 'Production'}
          labelPosition="before"
        />
      </div>
      <div className={styles.editorSurface}>
        {mode === 'ui' ? (
          <ParametersFormEditor
            value={rawJson}
            onChange={handleJsonChange}
            onValidityChange={state.setFormError}
            readOnly={readOnly}
          />
        ) : (
          <>
            <JsonEditor
              value={rawJson}
              onChange={handleJsonChange}
              fill
              hasError={!isValidJson}
              readOnly={readOnly}
            />
            {jsonError && <Text className={styles.jsonError}>{jsonError}</Text>}
          </>
        )}
      </div>
    </div>
  );
};

ParametersView.displayName = 'ParametersView';

export interface ParametersFooterActionsProps {
  state: ParametersEditorState;
}

/**
 * Save/Discard actions for the Parameters editor. Always renders so the
 * surrounding container's height stays stable; the buttons disable themselves
 * when their action isn't applicable.
 */
export const ParametersFooterActions = ({ state }: ParametersFooterActionsProps) => {
  const styles = useStyles();
  const {
    userCanEdit,
    permissionsLoading,
    hasChanges,
    isValidJson,
    formError,
    isSaving,
    handleSave,
    handleDiscard,
  } = state;
  const cantEditYet = !userCanEdit || permissionsLoading;
  const saveBlocked = cantEditYet || !hasChanges || !isValidJson || formError !== null || isSaving;

  return (
    <div className={styles.footerActions}>
      {formError && <Text className={styles.jsonError}>{formError}</Text>}
      <Button
        appearance="secondary"
        icon={<ArrowUndo20Regular />}
        onClick={handleDiscard}
        disabled={cantEditYet || !hasChanges || isSaving}
      >
        Discard
      </Button>
      <Button
        appearance="primary"
        icon={<Save20Regular />}
        onClick={handleSave}
        disabled={saveBlocked}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
};

ParametersFooterActions.displayName = 'ParametersFooterActions';
